import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ChangeEvent,
  type Dispatch,
  type ReactNode,
  type RefObject,
  type SetStateAction,
} from "react";
import type { HubCollection } from "@/lib/fetchStyles";
import type {
  HubFlowVersion,
  HubOpenStage,
} from "@/features/footwear-studio/HubFlowChain";
import type { OverlayOpenStageHandler } from "@/features/footwear-studio/OverlayFlowChain";

type FileUploadHandler = (e: ChangeEvent<HTMLInputElement>) => void;
type SelectStyleHandler = (styleId: string) => void | Promise<void>;
type SelectVersionHandler = (entry: HubFlowVersion) => void;
type TogglePinHandler = (jobId: string) => void;
type OpenStageHandler = OverlayOpenStageHandler;

interface StudioUploadContextValue {
  onFileUpload: FileUploadHandler | null;
  registerFileUpload: (handler: FileUploadHandler | null) => void;
  triggerFilePicker: () => void;
  /** Live Design Intent panel element (from StageAngles intentBoxRef). */
  getIntentPanelEl: () => HTMLElement | null;
  registerIntentPanelRef: (ref: RefObject<HTMLElement | null> | null) => void;
  /** True when a source image exists (primaryImage or sidePreviewImage). */
  hasSource: boolean;
  registerHasSource: (value: boolean) => void;
  /** Rail upload overlay open state (manual open when a design is loaded). */
  overlayOpen: boolean;
  setOverlayOpen: Dispatch<SetStateAction<boolean>>;
  /** HUB: collection-grouped style cards for overlay "From Designs" tab. */
  hubStyles: HubCollection[];
  registerHubStyles: (styles: HubCollection[]) => void;
  activeStyleId: string | null;
  registerActiveStyleId: (id: string | null) => void;
  onSelectStyle: SelectStyleHandler | null;
  registerSelectStyle: (handler: SelectStyleHandler | null) => void;
  versionHistory: HubFlowVersion[];
  versionsLoading: boolean;
  activeMasterJobId: string | null;
  pinnedJobIds: string[];
  onSelectVersion: SelectVersionHandler | null;
  onTogglePin: TogglePinHandler | null;
  onOpenStage: OpenStageHandler | null;
  registerChain: (v: {
    versionHistory: HubFlowVersion[];
    versionsLoading: boolean;
    activeMasterJobId: string | null;
    pinnedJobIds: string[];
    onSelectVersion: SelectVersionHandler | null;
    onTogglePin: TogglePinHandler | null;
    onOpenStage: OpenStageHandler | null;
  }) => void;
  /** True while overlay compact chain drill-down is visible. */
  chainOpen: boolean;
  registerChainOpen: (open: boolean) => void;
}

const StudioUploadContext = createContext<StudioUploadContextValue>({
  onFileUpload: null,
  registerFileUpload: () => {},
  triggerFilePicker: () => {},
  getIntentPanelEl: () => null,
  registerIntentPanelRef: () => {},
  hasSource: true,
  registerHasSource: () => {},
  overlayOpen: false,
  setOverlayOpen: () => {},
  hubStyles: [],
  registerHubStyles: () => {},
  activeStyleId: null,
  registerActiveStyleId: () => {},
  onSelectStyle: null,
  registerSelectStyle: () => {},
  versionHistory: [],
  versionsLoading: false,
  activeMasterJobId: null,
  pinnedJobIds: [],
  onSelectVersion: null,
  onTogglePin: null,
  onOpenStage: null,
  registerChain: () => {},
  chainOpen: false,
  registerChainOpen: () => {},
});

export function StudioUploadProvider({ children }: { children: ReactNode }) {
  const [onFileUpload, setOnFileUpload] = useState<FileUploadHandler | null>(null);
  const [hasSource, setHasSource] = useState(true);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [hubStyles, setHubStyles] = useState<HubCollection[]>([]);
  const [activeStyleId, setActiveStyleId] = useState<string | null>(null);
  const [onSelectStyle, setOnSelectStyle] = useState<SelectStyleHandler | null>(null);
  const [versionHistory, setVersionHistory] = useState<HubFlowVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [activeMasterJobId, setActiveMasterJobId] = useState<string | null>(null);
  const [pinnedJobIds, setPinnedJobIds] = useState<string[]>([]);
  const [onSelectVersion, setOnSelectVersion] = useState<SelectVersionHandler | null>(
    null,
  );
  const [onTogglePin, setOnTogglePin] = useState<TogglePinHandler | null>(null);
  const [onOpenStage, setOnOpenStage] = useState<OpenStageHandler | null>(null);
  const [chainOpen, setChainOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const intentPanelRefHolder = useRef<RefObject<HTMLElement | null> | null>(null);

  const registerFileUpload = useCallback((handler: FileUploadHandler | null) => {
    setOnFileUpload(() => handler);
  }, []);

  const registerIntentPanelRef = useCallback(
    (ref: RefObject<HTMLElement | null> | null) => {
      intentPanelRefHolder.current = ref;
    },
    [],
  );

  const getIntentPanelEl = useCallback(
    () => intentPanelRefHolder.current?.current ?? null,
    [],
  );

  const registerHasSource = useCallback((value: boolean) => {
    setHasSource(value);
  }, []);

  const registerHubStyles = useCallback((styles: HubCollection[]) => {
    setHubStyles(styles);
  }, []);

  const registerActiveStyleId = useCallback((id: string | null) => {
    setActiveStyleId(id);
  }, []);

  const registerSelectStyle = useCallback((handler: SelectStyleHandler | null) => {
    setOnSelectStyle(() => handler);
  }, []);

  const registerChain = useCallback(
    (v: {
      versionHistory: HubFlowVersion[];
      versionsLoading: boolean;
      activeMasterJobId: string | null;
      pinnedJobIds: string[];
      onSelectVersion: SelectVersionHandler | null;
      onTogglePin: TogglePinHandler | null;
      onOpenStage: OpenStageHandler | null;
    }) => {
      setVersionHistory(v.versionHistory);
      setVersionsLoading(v.versionsLoading);
      setActiveMasterJobId(v.activeMasterJobId);
      setPinnedJobIds(v.pinnedJobIds);
      setOnSelectVersion(() => v.onSelectVersion);
      setOnTogglePin(() => v.onTogglePin);
      setOnOpenStage(() => v.onOpenStage);
    },
    [],
  );

  const registerChainOpen = useCallback((open: boolean) => {
    setChainOpen(open);
  }, []);

  // Auto-close overlay once a source exists (unless chain drill-down).
  useEffect(() => {
    if (hasSource && !chainOpen) {
      setOverlayOpen(false);
    }
  }, [hasSource, chainOpen]);

  const triggerFilePicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onFileUpload?.(e);
      e.target.value = "";
    },
    [onFileUpload],
  );

  return (
    <StudioUploadContext.Provider
      value={{
        onFileUpload,
        registerFileUpload,
        triggerFilePicker,
        getIntentPanelEl,
        registerIntentPanelRef,
        hasSource,
        registerHasSource,
        overlayOpen,
        setOverlayOpen,
        hubStyles,
        registerHubStyles,
        activeStyleId,
        registerActiveStyleId,
        onSelectStyle,
        registerSelectStyle,
        versionHistory,
        versionsLoading,
        activeMasterJobId,
        pinnedJobIds,
        onSelectVersion,
        onTogglePin,
        onOpenStage,
        registerChain,
        chainOpen,
        registerChainOpen,
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png"
        onChange={handleChange}
        className="hidden"
        aria-hidden
        tabIndex={-1}
        data-testid="studio-rail-upload-input"
      />
      {children}
    </StudioUploadContext.Provider>
  );
}

export function useStudioUpload() {
  return useContext(StudioUploadContext);
}

/** Register upload handler from FootwearStudio; clears on unmount. */
export function useRegisterStudioFileUpload(
  handler: FileUploadHandler | null,
  deps: React.DependencyList = [],
) {
  const { registerFileUpload } = useContext(StudioUploadContext);

  useEffect(() => {
    registerFileUpload(handler);
    return () => registerFileUpload(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handler, registerFileUpload, ...deps]);
}

/**
 * Register StageAngles intentBoxRef for the rail upload overlay.
 * Call outside frozen leftBody JSX — only the RefObject is registered; no markup change.
 */
export function useRegisterStudioIntentPanel(
  panelRef: RefObject<HTMLElement | null>,
) {
  const { registerIntentPanelRef } = useContext(StudioUploadContext);

  useEffect(() => {
    registerIntentPanelRef(panelRef);
    return () => registerIntentPanelRef(null);
  }, [panelRef, registerIntentPanelRef]);
}

/**
 * Register whether a source image exists (StageAngles hasImage).
 * Call outside frozen leftBody JSX — effect only; no markup change.
 */
export function useRegisterStudioHasSource(hasSource: boolean) {
  const { registerHasSource } = useContext(StudioUploadContext);

  useEffect(() => {
    registerHasSource(hasSource);
    return () => registerHasSource(true);
  }, [hasSource, registerHasSource]);
}

/**
 * Register hub styles + select handler for overlay "From Designs" tab.
 * Call from FootwearStudio (same data path as Designs tab / StageEntry).
 */
export function useRegisterStudioHub(opts: {
  hubStyles: HubCollection[];
  activeStyleId: string | null;
  onSelectStyle: SelectStyleHandler | null;
}) {
  const {
    registerHubStyles,
    registerActiveStyleId,
    registerSelectStyle,
  } = useContext(StudioUploadContext);

  useEffect(() => {
    registerHubStyles(opts.hubStyles);
    registerActiveStyleId(opts.activeStyleId);
    registerSelectStyle(opts.onSelectStyle);
    return () => {
      registerHubStyles([]);
      registerActiveStyleId(null);
      registerSelectStyle(null);
    };
  }, [
    opts.hubStyles,
    opts.activeStyleId,
    opts.onSelectStyle,
    registerHubStyles,
    registerActiveStyleId,
    registerSelectStyle,
  ]);
}

/** Register hub chain data + handlers for overlay compact chain view. */
export function useRegisterStudioChain(opts: {
  versionHistory: HubFlowVersion[];
  versionsLoading: boolean;
  activeMasterJobId: string | null;
  pinnedJobIds: string[];
  onSelectVersion: SelectVersionHandler | null;
  onTogglePin: TogglePinHandler | null;
  onOpenStage: OpenStageHandler | null;
}) {
  const { registerChain } = useContext(StudioUploadContext);

  useEffect(() => {
    registerChain(opts);
    return () =>
      registerChain({
        versionHistory: [],
        versionsLoading: false,
        activeMasterJobId: null,
        pinnedJobIds: [],
        onSelectVersion: null,
        onTogglePin: null,
        onOpenStage: null,
      });
  }, [
    opts.versionHistory,
    opts.versionsLoading,
    opts.activeMasterJobId,
    opts.pinnedJobIds,
    opts.onSelectVersion,
    opts.onTogglePin,
    opts.onOpenStage,
    registerChain,
  ]);
}

/** Register whether overlay compact chain drill-down is open (gates auto-close). */
export function useRegisterStudioChainOpen(chainOpen: boolean) {
  const { registerChainOpen } = useContext(StudioUploadContext);

  useEffect(() => {
    registerChainOpen(chainOpen);
    return () => registerChainOpen(false);
  }, [chainOpen, registerChainOpen]);
}
